import {
    ActionBar,
    ActionButton,
    BottomInfoBar,
    BottomInfoElement,
    Description,
    DialogWrapper,
    FileImages,
    InfoText,
    Tags,
    TagWrapper,
    ThumbnailWrapper
} from './FileDetailsDialog.style';
import React, {useContext, useEffect, useState} from 'react';
import {Button} from '@bosch/react-frok';
import {StorageObject} from '../../../models/StorageObject';
import {t} from 'i18next';
import {executeAction, formatDate, formatFiletype, formatSize, getFontSize, isNullOrEmpty} from '../../../utils';
import {getLabel, isFile} from '../../../utils/StorageObjectUtils';
import {isFileDownloadable} from '../../../utils/DtoUtils';
import {useGetThumbnailUrl} from '../../../hooks/UseGetThumbnailUrl';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {AuthContext} from '../../../context/AuthContext';
import {hasWriteOwnerAccess} from '../../../utils/UserUtils';
import {getApplicableTemplateMetadataPropertySettingsMap, isVisibleComponent} from '../../../utils/TemplateUtils';
import {
    STORAGE_OBJECT_NAME,
    STORAGE_OBJECT_OBJECT_IS_PUBLIC,
    STORAGE_OBJECT_OBJECT_LABELS,
    STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION,
    STORAGE_OBJECT_OBJECT_LABELS_NAME,
    STORAGE_OBJECT_OBJECT_METADATA,
    STORAGE_OBJECT_OBJECT_METADATA_CONTENT_TYPE,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_UPDATED,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_FROM,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_TO,
    STORAGE_OBJECT_OBJECT_METADATA_TAGS,
    STORAGE_OBJECT_OBJECT_METADATA_THUMBNAIL_URL,
    STORAGE_OBJECT_TOTAL_SIZE
} from '../../../init/storage_object_constants';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {ObjectType} from '../../../models/ObjectType';

export interface FileDetailsDialogProps {
    readonly item: StorageObject;
    readonly language: string;
    readonly defaultLocale: string;
    readonly editMode: boolean;
    editAction: () => void;
    deleteAction: () => void;
    downloadAction: () => void;
    copyUrlAction: () => void;
    onClose: () => void;
}

/**
 * @name FileDetailsDialog
 * @description
 * The dialog containing information to the file that has been clicked
 */
export const FileDetailsDialog: React.VFC<FileDetailsDialogProps> = (props) => {
    const {isAuthenticated, roles} = useContext(AuthContext);
    const [isAuthorizedWriteOwnerAccess] = useState<boolean>(hasWriteOwnerAccess(isAuthenticated, roles, props.item.metadata.ownerRoles, props.item.metadata.parentOwnerRoles));
    const isEditModeAuthorized = (props.editMode && isAuthorizedWriteOwnerAccess) as boolean;
    const [templatePropertySettings] = useState<Map<string, TemplatePropertySettings>>(getApplicableTemplateMetadataPropertySettingsMap(ObjectType.FILE, props.item.templateId));
    const isFileItem = isFile(props.item.objectType);
    const getThumbnailUrl = useGetThumbnailUrl();
    const [thumbnail, setThumbnail] = useState('');
    const setExistingThumbnail = async (thumbnailUrl: string) => {
        if (isNullOrEmpty(thumbnailUrl)) {
            return;
        }

        await getThumbnailUrl(thumbnailUrl)
            .then((existingThumbnail) => {
                if (!isNullOrEmpty(existingThumbnail)) {
                    setThumbnail(existingThumbnail);
                }
            })
            .catch();
    };

    useEffect(() => {
        if ((props.item.metadata.thumbnailUrl) && (!isNullOrEmpty(props.item.metadata.thumbnailUrl))) {
            setExistingThumbnail(props.item.metadata.thumbnailUrl).catch();
        }
    }, [props.item.metadata.thumbnailUrl]);

    let label = getLabel(props.defaultLocale, props.item.labels);
    let isDownloadable = (isFileItem && isFileDownloadable(props.item.status)) as boolean;

    let fontSize = getFontSize([
        t('GENERAL_DOWNLOAD_LABEL'),
    ], 4);

    return (
        <DialogWrapper id={generateIdentifier()}
                       key={props.item.id}
                       modal
                       open
                       title={t('FILE_DETAILS_DIALOG_TITLE')}
                       headline={(isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, templatePropertySettings)
                           && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, templatePropertySettings)
                           && label.name) ? label.name : t('GENERAL_UNKNOWN_CONTENT_TEXT')}
                       headlineSpace={props.editMode}
                       onClose={props.onClose}
                       confirmButton={isDownloadable ?
                           <ActionButton id={generateIdentifier()}
                                         key='download'
                                         icon='download'
                                         mode='primary'
                                         type='button'
                                         label={t('GENERAL_DOWNLOAD_LABEL')}
                                         title={t('GENERAL_DOWNLOAD_LABEL')}
                                         fontSize={fontSize}
                           /> : undefined}
                       onConfirm={(e) => isDownloadable ? executeAction(e, props.downloadAction) : props.onClose}>
            <ActionBar id={generateIdentifier()}
                       key={'command-bar'}
                       actions={[
                           <Button id={generateIdentifier()}
                                   key='copy-button'
                                   icon='copy'
                                   mode='tertiary'
                                   type='button'
                                   aria-label={t('GENERAL_COPY_URL_LABEL')}
                                   title={t('GENERAL_COPY_URL_LABEL')}
                                   onClick={props.copyUrlAction}/>,
                           isEditModeAuthorized &&
                           <Button id={generateIdentifier()}
                                   key='edit'
                                   icon='edit'
                                   mode='tertiary'
                                   type='button'
                                   title={t('GENERAL_EDIT_LABEL')}
                                   onClick={props.editAction}/>,
                           isEditModeAuthorized &&
                           <Button id={generateIdentifier()}
                                   key='delete'
                                   icon='delete'
                                   mode='tertiary'
                                   type='button'
                                   title={t('GENERAL_DELETE_LABEL')}
                                   onClick={props.deleteAction}/>
                       ]}
            />
            {isVisibleComponent(STORAGE_OBJECT_NAME, templatePropertySettings) &&
                <InfoText id={generateIdentifier()}>
                    {t('UPLOAD_FILE_META_DATA_DIALOG_ORIGINAL_NAME_INFO_TEXT', {name: props.item.name ? props.item.name : t('GENERAL_UNKNOWN_CONTENT_TEXT')})}
                </InfoText>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_NAME, templatePropertySettings)) &&
                <InfoText id={generateIdentifier()}>
                    {t('FILE_DETAILS_DIALOG_FILE_NAME_TEXT', {name: label.name ? label.name : t('GENERAL_UNKNOWN_CONTENT_TEXT')})}
                </InfoText>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_CONTENT_TYPE, templatePropertySettings)) &&
                <InfoText id={generateIdentifier()}>
                    {t('FILE_DETAILS_DIALOG_FILE_TYPE_INFO_TEXT', {
                        type: props.item.metadata.contentType ? formatFiletype(props.item.metadata.contentType) : t('GENERAL_UNKNOWN_CONTENT_TEXT')
                    })}
                </InfoText>
            }
            {(props.editMode && isVisibleComponent(STORAGE_OBJECT_OBJECT_IS_PUBLIC, templatePropertySettings)) &&
                <InfoText id={generateIdentifier()}>
                    {t('FILE_DETAILS_PUBLIC_ACCESS', {isPublic: props.item.isPublic ? t('PUBLIC_ACCESS_TRUE_VALUE') : t('PUBLIC_ACCESS_FALSE_VALUE')})}
                </InfoText>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_CREATED, templatePropertySettings)) &&
                <InfoText id={generateIdentifier()}>
                    {t('FILE_DETAILS_DIALOG_DATE_CREATED_INFO_TEXT', {date: props.item.metadata.dateCreated ? formatDate(props.language, props.item.metadata.dateCreated) : t('GENERAL_UNKNOWN_DATE_TEXT')})}
                </InfoText>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_UPDATED, templatePropertySettings)) &&
                <InfoText id={generateIdentifier()}>
                    {t('FILE_DETAILS_DIALOG_DATE_UPDATED_INFO_TEXT', {date: props.item.metadata.dateUpdated ? formatDate(props.language, props.item.metadata.dateUpdated) : t('GENERAL_UNKNOWN_DATE_TEXT')})}
                </InfoText>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_FROM, templatePropertySettings)) &&
                <InfoText id={generateIdentifier()}>
                    {t('DATE_FROM_INFO_TEXT', {date: props.item.metadata.dateValidFrom ? formatDate(props.language, props.item.metadata.dateValidFrom) : t('GENERAL_UNKNOWN_DATE_TEXT')})}
                </InfoText>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_TO, templatePropertySettings)) &&
                <InfoText id={generateIdentifier()}>
                    {t('DATE_TO_INFO_TEXT', {date: props.item.metadata.dateValidTo ? formatDate(props.language, props.item.metadata.dateValidTo) : t('GENERAL_UNKNOWN_DATE_TEXT')})}
                </InfoText>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_LABELS_DESCRIPTION, templatePropertySettings)) &&
                <Description id={generateIdentifier()}>
                    {t('DESCRIPTION_INFO_TEXT', {description: label.description ? label.description : t('GENERAL_UNKNOWN_CONTENT_TEXT')})}
                </Description>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_TAGS, templatePropertySettings)) &&
                <TagWrapper id={generateIdentifier()}>
                    {props.item.metadata.tags?.map(tag => {
                        return <Tags id={generateIdentifier()}
                                     key={tag}
                                     label={tag}/>;
                    })}
                </TagWrapper>
            }
            {(props.item.metadata.thumbnailUrl
                && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_THUMBNAIL_URL, templatePropertySettings))) && (
                <ThumbnailWrapper id={generateIdentifier()}>
                    <FileImages id={generateIdentifier()}
                                src={thumbnail}
                                alt={'thumbnail'}/>
                </ThumbnailWrapper>
            )}
            {isVisibleComponent(STORAGE_OBJECT_TOTAL_SIZE, templatePropertySettings) &&
                <BottomInfoBar id={generateIdentifier()}>
                    {isVisibleComponent(STORAGE_OBJECT_TOTAL_SIZE, templatePropertySettings) &&
                        <BottomInfoElement
                            id={generateIdentifier()}>{formatSize(props.item.totalSize)}</BottomInfoElement>
                    }
                </BottomInfoBar>
            }
        </DialogWrapper>
    );
};
